import styled from "styled-components";
import { MONTH_ROW_MIN_HEIGHT } from "./theme";

type ActiveProps = {
    active?: boolean;
};

type TodayProps = {
    today?: boolean;
};

type HeightProps = {
    height?: number;
};

type RowHeightProps = {
    rowHeight?: number;
};

type ColorProps = {
    color: string;
};

type BackgroundProps = {
    background: string;
    color?: string;
};

type OffsetProps = {
    offset?: string | number;
};

type MutedProps = {
    muted?: boolean;
};

export const CalendarHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.8rem 1rem;
    border-bottom: 1px solid var(--calendar-border);
    flex-wrap: wrap;
    background: var(--calendar-shell);
`;

export const WeekdaysRow = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    align-items: center;
    padding: 0 0.7rem;
    border-bottom: 1px solid var(--calendar-border);
    background: var(--calendar-shell);
`;

export const MonthHeading = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.15rem;
    font-weight: 800;
`;

export const NavArrow = styled.button`
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    color: var(--calendar-text);
    width: 28px;
    height: 28px;
    border-radius: var(--calendar-radius-control);
    cursor: pointer;
`;

export const TodayButton = styled.button`
    border: none;
    background: var(--calendar-accent-soft);
    color: var(--calendar-accent);
    padding: 0.45rem 0.7rem;
    border-radius: var(--calendar-radius-control);
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
`;

export const ViewTabs = styled.div`
    display: inline-flex;
    gap: 0.35rem;
    align-items: center;
    padding: 0.2rem;
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    border-radius: var(--calendar-radius-control);
`;

export const ViewTab = styled.button<ActiveProps>`
    border: none;
    background: ${p => p.active ? "var(--calendar-accent)" : "transparent"};
    color: ${p => p.active ? "var(--calendar-bg)" : "var(--calendar-text-soft)"};
    height: 34px;
    min-width: 56px;
    border-radius: var(--calendar-radius-control);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;

export const CalendarGridWrap = styled.div`
    display: grid;
    grid-auto-rows: auto;
    gap: 0;
    height: 100%;
    align-content: start;
`;

export const MonthWeekRow = styled.div<RowHeightProps>`
    position: relative;
    height: ${p => `${p.rowHeight || MONTH_ROW_MIN_HEIGHT}px`};
`;

export const MonthWeekCells = styled.div<RowHeightProps & { barLanes?: number }>`
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    grid-auto-rows: ${p => `${p.rowHeight || MONTH_ROW_MIN_HEIGHT}px`};
    height: ${p => `${p.rowHeight || MONTH_ROW_MIN_HEIGHT}px`};
`;

export const MonthSpanLayer = styled.div`
    position: absolute;
    top: 34px;
    left: 0;
    right: 0;
    z-index: 2;
    pointer-events: none;
    display: grid;
    gap: 6px;
    padding: 0;
`;

export const MonthSpanRow = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    align-items: center;
`;

export const MonthSpanItem = styled.button<BackgroundProps>`
    pointer-events: auto;
    height: 22px;
    min-height: 22px;
    border: none;
    border-radius: var(--calendar-radius-task);
    background: ${p => p.background};
    color: ${p => p.color || "var(--calendar-text)"};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.45rem;
    padding: 0 0.45rem;
    margin: 0 0.55rem;
    box-sizing: border-box;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.01em;
    cursor: pointer;
    overflow: hidden;
`;

export const MonthSpanLead = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
    overflow: hidden;
`;

export const MonthSpanBullet = styled.span<ColorProps>`
    width: 4px;
    height: 4px;
    border-radius: var(--calendar-radius-round);
    background: ${p => p.color};
    flex: 0 0 auto;
`;

export const DayAgenda = styled.div`
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    align-items: stretch;
    height: 100%;
`;

export const DayHourLabel = styled.div`
    min-height: 64px;
    padding: 0.55rem 0.7rem 0 0;
    text-align: right;
    font-size: 11px;
    font-weight: 700;
    color: var(--calendar-text-soft);
    border-right: 1px solid var(--calendar-border);
    border-bottom: 1px solid var(--calendar-border);
    background: var(--calendar-shell);
`;

export const DayHourRow = styled.div<ActiveProps>`
    min-height: 64px;
    border-bottom: 1px solid ${p => p.active ? "var(--calendar-selected-cell-border)" : "var(--calendar-border)"};
    padding: 0.35rem 0.5rem;
    background: ${p => p.active ? "var(--calendar-selected-cell-bg)" : "var(--calendar-cell)"};
    display: grid;
    gap: 0.3rem;
    align-content: start;
    border-radius: var(--calendar-radius-cell);
`;

export const WeekGrid = styled.div`
    display: grid;
    grid-template-columns: 72px repeat(7, minmax(0, 1fr));
    height: 100%;
`;

export const WeekHourCorner = styled.div`
    border-right: 1px solid var(--calendar-border);
    border-bottom: 1px solid var(--calendar-border);
    background: var(--calendar-shell);
`;

export const WeekHeaderCell = styled.button<ActiveProps>`
    border: none;
    border-right: 1px solid var(--calendar-border);
    border-bottom: 1px solid ${p => p.active ? "var(--calendar-selected-cell-border)" : "var(--calendar-border)"};
    background: ${p => p.active ? "var(--calendar-selected-cell-bg)" : "var(--calendar-shell)"};
    color: var(--calendar-text);
    padding: 0.7rem 0.4rem;
    display: grid;
    gap: 0.2rem;
    justify-items: center;
    cursor: pointer;
    border-radius: var(--calendar-radius-cell);
`;

export const WeekHeaderTitle = styled.div`
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: var(--calendar-text-soft);
`;

export const WeekHeaderDate = styled.div<TodayProps>`
    width: 28px;
    height: 28px;
    border-radius: var(--calendar-radius-cell);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${p => p.today ? "var(--calendar-today-marker-bg)" : "transparent"};
    color: ${p => p.today ? "var(--calendar-today-marker-text)" : "var(--calendar-text)"};
    font-size: 13px;
    font-weight: 800;
`;

export const WeekHourLabel = styled.div`
    min-height: 58px;
    padding: 0.45rem 0.7rem 0 0;
    text-align: right;
    font-size: 10px;
    font-weight: 700;
    color: var(--calendar-text-soft);
    border-right: 1px solid var(--calendar-border);
    border-bottom: 1px solid var(--calendar-border);
    background: var(--calendar-shell);
`;

export const WeekAllDayLabel = styled(WeekHourLabel)<HeightProps>`
    padding-top: 0.7rem;
    min-height: ${p => `${p.height || 48}px`};
`;

export const WeekAllDayArea = styled.div<HeightProps>`
    grid-column: span 7;
    position: relative;
    border-right: 1px solid var(--calendar-border);
    border-bottom: 1px solid var(--calendar-border);
    background: var(--calendar-cell);
    min-height: ${p => `${p.height || 48}px`};
    border-radius: var(--calendar-radius-cell);
`;

export const WeekAllDayLayer = styled.div`
    display: grid;
    gap: 6px;
    padding: 0.45rem 0;
`;

export const WeekAllDayRow = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    align-items: center;
`;

export const WeekSlot = styled.button<ActiveProps>`
    border: none;
    border-right: 1px solid var(--calendar-border);
    border-bottom: 1px solid ${p => p.active ? "var(--calendar-selected-cell-border)" : "var(--calendar-border)"};
    background: ${p => p.active ? "var(--calendar-selected-cell-bg)" : "var(--calendar-cell)"};
    min-height: 58px;
    padding: 0.28rem;
    display: grid;
    gap: 0.22rem;
    align-content: start;
    text-align: left;
    cursor: pointer;
    border-radius: var(--calendar-radius-cell);
`;

export const YearGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
    padding: 1rem;

    @media (max-width: 980px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

export const YearMonthCard = styled.div`
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    border-radius: var(--calendar-radius-card);
    overflow: hidden;
`;

export const YearMonthHeader = styled.div`
    padding: 0.85rem 0.95rem;
    border-bottom: 1px solid var(--calendar-border);
    font-size: 13px;
    font-weight: 800;
`;

export const YearMonthGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    padding: 0.55rem;
`;

export const YearWeekday = styled.div`
    font-size: 9px;
    font-weight: 800;
    color: var(--calendar-text-soft);
    text-align: center;
    padding-bottom: 0.35rem;
`;

export const YearDayCell = styled.button<ActiveProps & MutedProps>`
    border: 1px solid ${p => p.active ? "var(--calendar-selected-cell-border)" : "transparent"};
    background: ${p => p.active ? "var(--calendar-selected-cell-bg)" : "transparent"};
    color: ${p => p.muted ? "var(--calendar-text-soft)" : "var(--calendar-text)"};
    height: 34px;
    border-radius: var(--calendar-radius-cell);
    cursor: pointer;
    display: grid;
    place-items: center;
    position: relative;
    font-size: 11px;
    font-weight: 700;
`;

export const YearDayDot = styled.span<ColorProps>`
    width: 5px;
    height: 5px;
    border-radius: var(--calendar-radius-round);
    background: ${p => p.color};
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
`;

export const WeekdayHeader = styled.div<{ weekend?: boolean }>`
    padding: 0.5rem 0.35rem 0.45rem;
    font-size: 11px;
    color: ${p => p.weekend ? "#dd7c7c" : "var(--calendar-text-soft)"};
    font-weight: 700;
    text-transform: uppercase;
    text-align: center;
    letter-spacing: 0.08em;
`;

export const DayCell = styled.button<ActiveProps & RowHeightProps>`
    border: none;
    border-right: 1px solid var(--calendar-border);
    border-bottom: 1px solid var(--calendar-border);
    background: ${p => p.active ? "var(--calendar-selected-cell-bg)" : "var(--calendar-cell)"};
    text-align: left;
    padding: 0.55rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    cursor: pointer;
    color: var(--calendar-text);
    box-shadow: ${p => p.active ? "inset 0 0 0 1px var(--calendar-selected-cell-border)" : "none"};
    height: ${p => `${p.rowHeight || MONTH_ROW_MIN_HEIGHT}px`};
    overflow: hidden;
    position: relative;
    border-radius: var(--calendar-radius-cell);
`;

export const DayCellItems = styled.div<OffsetProps>`
    position: absolute;
    left: 0.55rem;
    right: 0.55rem;
    top: ${p => typeof p.offset === "number" ? `${p.offset}rem` : (p.offset || "2.1rem")};
    bottom: 1.9rem;
    display: grid;
    gap: 4px;
    align-content: start;
    overflow: hidden;
`;

export const DayNumber = styled.div<TodayProps & MutedProps>`
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${p => p.today ? "22px" : "auto"};
    height: ${p => p.today ? "22px" : "auto"};
    border-radius: var(--calendar-radius-cell);
    background: ${p => p.today ? "var(--calendar-today-marker-bg)" : "transparent"};
    color: ${p => p.today ? "var(--calendar-today-marker-text)" : p.muted ? "var(--calendar-text-soft)" : "var(--calendar-text)"};
    font-size: 12px;
    font-weight: 700;
    margin: 0.05rem 0 0.45rem 0.05rem;
`;

export const ItemPill = styled.button<BackgroundProps>`
    width: 100%;
    height: 22px;
    min-height: 22px;
    border: none;
    border-radius: var(--calendar-radius-task);
    padding: 0 0.45rem;
    text-align: left;
    cursor: pointer;
    background: ${p => p.background};
    color: ${p => p.color || "var(--calendar-text)"};
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    overflow: hidden;
    box-sizing: border-box;
`;

export const ItemBullet = styled.span<ColorProps>`
    width: 4px;
    height: 4px;
    border-radius: var(--calendar-radius-round);
    background: ${p => p.color};
    flex: 0 0 auto;
`;

export const MoreText = styled.div`
    font-size: 10px;
    color: var(--calendar-text-soft);
    line-height: 1.2;
    padding-top: 0.05rem;
    min-height: 12px;
`;

export const DayCellFooter = styled.div`
    position: absolute;
    left: 0.55rem;
    right: 0.55rem;
    bottom: 0.45rem;
    display: flex;
    justify-content: flex-start;
    pointer-events: none;
`;

export const StaticCellLabel = styled.div<MutedProps>`
    max-width: 100%;
    border-radius: var(--calendar-radius-badge);
    padding: 0.16rem 0.5rem;
    background: ${p => p.muted ? "rgba(148, 163, 184, 0.14)" : "var(--calendar-accent-soft)"};
    color: ${p => p.muted ? "var(--calendar-text-soft)" : "var(--calendar-accent)"};
    font-size: 9px;
    font-weight: 800;
    line-height: 1.15;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;
